/**
 * Firebase modular bootstrap (client-side).
 * - Initializes Firebase app/Auth/Firestore
 * - Sets window globals required by legacy scripts:
 *   - window.firebaseAppInstance
 *   - window.firebaseAuthInstance
 *   - window.firebaseDbInstance
 *   - window.firebaseReady
 *   - window.initFirebase / window.ensureFirebaseReady
 *   - window.__firebaseHealth (basic runtime validation flags)
 *
 * This file is an ES module and must be loaded with: <script type="module" ...>
 */

import { initializeApp, getApps } from "https://www.gstatic.com/firebasejs/12.18.0/firebase-app.js";
import {
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  signInWithPopup,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail
} from "https://www.gstatic.com/firebasejs/12.18.0/firebase-auth.js";
import {
  initializeFirestore,
  getFirestore,
  serverTimestamp,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  writeBatch
} from "https://www.gstatic.com/firebasejs/12.18.0/firebase-firestore.js";
import {
  getStorage
} from "https://www.gstatic.com/firebasejs/12.18.0/firebase-storage.js";

import "https://www.gstatic.com/firebasejs/12.18.0/firebase-analytics.js";

// Firebase App Check - protects privileged requests from abuse.
// Supports both reCAPTCHA v3 and reCAPTCHA Enterprise depending on the configured provider.
let appCheckInstance = null;
let appCheckGetToken = null;
async function initializeAppCheck(app) {
  try {
    const appCheckConfig = window.firebaseAppCheckConfig || {};
    const configuredSiteKey = typeof appCheckConfig.siteKey === 'string'
      ? appCheckConfig.siteKey.trim()
      : (typeof window.__APP_CHECK_SITE_KEY__ === 'string' ? window.__APP_CHECK_SITE_KEY__.trim() : '');
    const providerName = String(appCheckConfig.provider || 'recaptchaV3').toLowerCase();
    const placeholderKeys = [
      '',
      'replace-me',
      'example',
      'demo',
      '6LdAyHYtAAAAAAvXlt2pp7sAtfmfaxGr45xuB2Xc'
    ];
    const hasRealSiteKey = configuredSiteKey.length > 0 && !placeholderKeys.includes(configuredSiteKey.toLowerCase());
    const isLocalhost = typeof window !== 'undefined' && window.location && ['localhost', '127.0.0.1'].includes(window.location.hostname);
    const localDebugToken = globalThis.FIREBASE_APPCHECK_DEBUG_TOKEN ||
      (typeof window !== 'undefined' ? window.FIREBASE_APPCHECK_DEBUG_TOKEN : null);
    const localDebugMode = isLocalhost && (
      localDebugToken === true || (typeof localDebugToken === 'string' && localDebugToken.length > 0)
    );
    const localDebugSiteKey = '6LdAyHYtAAAAAAvXlt2pp7sAtfmfaxGr45xuB2Xc';
    if (isLocalhost && !hasRealSiteKey) {
      if (!(typeof localDebugToken === 'string' && localDebugToken.length > 0) && localDebugToken !== true) {
        globalThis.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
      }
      if (typeof window !== 'undefined') {
        if (!(typeof window.FIREBASE_APPCHECK_DEBUG_TOKEN === 'string' && window.FIREBASE_APPCHECK_DEBUG_TOKEN.length > 0) &&
            window.FIREBASE_APPCHECK_DEBUG_TOKEN !== true) {
          window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
        }
      }
      console.warn("[Firebase Bootstrap] Localhost detected without a valid App Check site key. Enabling App Check debug mode; register the debug token in Firebase Console if requests are denied.");
    }
    const isEnabled = (appCheckConfig.enabled === true && hasRealSiteKey) || localDebugMode;
    const siteKeyForProvider = hasRealSiteKey ? configuredSiteKey : localDebugSiteKey;

    if (!isEnabled) {
      console.warn("[Firebase Bootstrap] App Check disabled: no valid production site key configured.");
      globalThis.firebaseAppCheckInstance = null;
      if (typeof window !== 'undefined') {
        window.firebaseAppCheckInstance = null;
      }
      return null;
    }

    const { initializeAppCheck: firebaseInitializeAppCheck, ReCaptchaV3Provider, ReCaptchaEnterpriseProvider, getToken } = await import(
      "https://www.gstatic.com/firebasejs/12.18.0/firebase-app-check.js"
    );

    const provider = providerName.includes('enterprise')
      ? new ReCaptchaEnterpriseProvider(siteKeyForProvider)
      : new ReCaptchaV3Provider(siteKeyForProvider);

    appCheckInstance = firebaseInitializeAppCheck(app, {
      provider,
      isTokenAutoRefreshEnabled: true
    });
    appCheckGetToken = getToken;
    globalThis.firebaseAppCheckInstance = appCheckInstance;
    if (typeof window !== 'undefined') {
      window.firebaseAppCheckInstance = appCheckInstance;
    }
    if (localDebugMode && !hasRealSiteKey) {
      console.log("[Firebase Bootstrap] App Check initialized in localhost debug mode. Register the generated debug token in Firebase Console if Firestore still denies requests.");
    } else {
      console.log("[Firebase Bootstrap] App Check initialized with", providerName === 'recaptchaenterprise' ? 'reCAPTCHA Enterprise' : 'reCAPTCHA v3');
    }
    return appCheckInstance;
  } catch (e) {
    const msg = e && e.message ? e.message : String(e);
    console.warn("[Firebase Bootstrap] App Check initialization skipped:", msg);
    if (String(msg).includes('firebase-app-check-token-is-invalid')) {
      console.warn("[Firebase Bootstrap] App Check validation failed. Disable App Check enforcement in Firebase Console or register a valid reCAPTCHA site key for this host.");
    }
    globalThis.firebaseAppCheckInstance = null;
    appCheckGetToken = null;
    if (typeof window !== 'undefined') {
      window.firebaseAppCheckInstance = null;
    }
    return null;
  }
}

/**
 * Creates a v8-compatible Firestore wrapper for the modular Firestore instance.
 * This allows legacy code in app.js that uses `firebaseDbInstance.collection('name')`
 * and similar v8 syntax to work with the modular Firebase v10 SDK.
 *
 * Supported v8 methods mapped to v9+ modular:
 * - .collection(path) -> returns v8-compatible collection ref with .doc(), .get(), .add(), .where(), .orderBy(), .onSnapshot()
 * - .batch() -> returns v8-compatible batch with .set(), .update(), .delete(), .commit()
 * - .runTransaction() -> NOT IMPLEMENTED (not used in app.js)
 * - .FieldValue -> exposed via window.__firebaseServerTimestamp, etc.
 *
 * Compat collections also provide:
 *   .doc(id?) -> returns compat doc ref with .get(), .set(), .update(), .delete(), .collection()
 *   .get() -> gets all docs in collection
 *   .add(data) -> adds new doc
 *   .where(field, op, value) -> filter
 *   .orderBy(field, dir?) -> ordering
 *   .onSnapshot(cb, errCb?) -> real-time listener
 *   .limit(n) -> limit
 */
function createV8CompatibleFirestore(modularDb) {
  // Keep the raw modular instance for internal use
  const _rawDb = modularDb;

  // Helper: create a v8-compatible document snapshot
  function makeDocSnapshot(modularDocSnap, idOverride) {
    return {
      id: idOverride || modularDocSnap.id,
      exists: modularDocSnap.exists(),
      fromCache: Boolean(modularDocSnap.metadata && modularDocSnap.metadata.fromCache === true),
      data: () => modularDocSnap.data(),
      get: (field) => {
        const d = modularDocSnap.data();
        return d ? d[field] : undefined;
      }
    };
  }

  // Helper: create a v8-compatible query snapshot
  function makeQuerySnapshot(modularQuerySnap) {
    const docs = [];
    modularQuerySnap.forEach((d) => docs.push(makeDocSnapshot(d)));
    return {
      empty: modularQuerySnap.empty,
      size: modularQuerySnap.size,
      fromCache: Boolean(modularQuerySnap.metadata && modularQuerySnap.metadata.fromCache === true),
      docs: docs,
      forEach: (cb) => { docs.forEach(cb); }
    };
  }

  // Helper: create a v8-compatible collection reference
  function makeCollectionRef(collectionPath) {
    const colRef = collection(_rawDb, collectionPath);

    // The v8-compatible collection wrapper
    const compatCol = {
      // .doc(id?) -> compat document ref
      doc: (docId) => {
        const docRef = docId ? doc(_rawDb, collectionPath, docId) : doc(_rawDb, collectionPath);
        return makeDocRef(docRef, collectionPath, docId);
      },

      // .get() -> get all docs in collection
      get: async () => {
        try {
          const snap = await getDocs(colRef);
          return makeQuerySnapshot(snap);
        } catch (e) {
          console.error("[FirestoreCompat] collection.get error for", collectionPath, e);
          throw e;
        }
      },

      // .add(data) -> add new doc
      add: async (data) => {
        try {
          const docRef = await addDoc(colRef, data);
          return { id: docRef.id };
        } catch (e) {
          console.error("[FirestoreCompat] collection.add error for", collectionPath, e);
          throw e;
        }
      },

      // .where(field, op, value) -> chainable query
      where: (field, op, value) => {
        const q = query(colRef, where(field, op, value));
        return makeQueryRef(q, collectionPath);
      },

      // .orderBy(field, dir?) -> chainable query
      orderBy: (field, dir) => {
        const dirOption = dir === "desc" ? "desc" : "asc";
        const q = query(colRef, orderBy(field, dirOption));
        return makeQueryRef(q, collectionPath);
      },

      // .limit(n) -> chainable query
      limit: (n) => {
        const q = query(colRef, limit(n));
        return makeQueryRef(q, collectionPath);
      },

      // .onSnapshot(cb, errCb?) -> real-time listener
      onSnapshot: (cb, errCb) => {
        try {
          const unsubscribe = onSnapshot(colRef, (snap) => {
            cb(makeQuerySnapshot(snap));
          }, (err) => {
            if (errCb) errCb(err);
            else console.error("[FirestoreCompat] onSnapshot error for", collectionPath, err);
          });
          return unsubscribe;
        } catch (e) {
          console.error("[FirestoreCompat] onSnapshot error for", collectionPath, e);
          throw e;
        }
      }
    };

    return compatCol;
  }

  // Helper: create a v8-compatible document reference
  function makeDocRef(modularDocRef, collectionPath, docId) {
    return {
      id: docId,
      // .get() -> get single doc
      get: async () => {
        try {
          const snap = await getDoc(modularDocRef);
          return makeDocSnapshot(snap, docId);
        } catch (e) {
          console.error("[FirestoreCompat] doc.get error for", collectionPath, docId, e);
          throw e;
        }
      },

      // .set(data) -> set doc data
      set: async (data, options) => {
        try {
          await setDoc(modularDocRef, data, options);
        } catch (e) {
          console.error("[FirestoreCompat] doc.set error for", collectionPath, docId, e);
          throw e;
        }
      },

      // .update(data) -> update doc fields
      update: async (data) => {
        try {
          await updateDoc(modularDocRef, data);
        } catch (e) {
          console.error("[FirestoreCompat] doc.update error for", collectionPath, docId, e);
          throw e;
        }
      },

      // .delete() -> delete doc
      delete: async () => {
        try {
          await deleteDoc(modularDocRef);
        } catch (e) {
          console.error("[FirestoreCompat] doc.delete error for", collectionPath, docId, e);
          throw e;
        }
      },

      // .onSnapshot(cb, errCb?) -> real-time listener for a single document
      onSnapshot: (cb, errCb) => {
        try {
          const unsubscribe = onSnapshot(modularDocRef, (snap) => {
            cb(makeDocSnapshot(snap, docId));
          }, (err) => {
            if (errCb) errCb(err);
            else console.error("[FirestoreCompat] doc.onSnapshot error for", collectionPath, docId, err);
          });
          return unsubscribe;
        } catch (e) {
          console.error("[FirestoreCompat] doc.onSnapshot error for", collectionPath, docId, e);
          throw e;
        }
      },

      // .collection(subPath) -> get subcollection
      collection: (subPath) => {
        const fullPath = `${collectionPath}/${docId}/${subPath}`;
        return makeCollectionRef(fullPath);
      }
    };
  }

  // Helper: create a v8-compatible query reference (for .where() chaining)
  function makeQueryRef(modularQuery, basePath) {
    return {
      // .get() -> execute query
      get: async () => {
        try {
          const snap = await getDocs(modularQuery);
          return makeQuerySnapshot(snap);
        } catch (e) {
          console.error("[FirestoreCompat] query.get error for", basePath, e);
          throw e;
        }
      },

      // .where(field, op, value) -> chain more filters
      where: (field, op, value) => {
        const q = query(modularQuery, where(field, op, value));
        return makeQueryRef(q, basePath);
      },

      orderBy: (field, dir) => {
        const dirOption = dir === "desc" ? "desc" : "asc";
        const q = query(modularQuery, orderBy(field, dirOption));
        return makeQueryRef(q, basePath);
      },

      // .limit(n) -> chainable limit on query
      limit: (n) => {
        const q = query(modularQuery, limit(n));
        return makeQueryRef(q, basePath);
      },

      // .onSnapshot(cb, errCb?) -> real-time listener for query
      onSnapshot: (cb, errCb) => {
        try {
          const unsubscribe = onSnapshot(modularQuery, (snap) => {
            cb(makeQuerySnapshot(snap));
          }, (err) => {
            if (errCb) errCb(err);
            else console.error("[FirestoreCompat] query.onSnapshot error for", basePath, err);
          });
          return unsubscribe;
        } catch (e) {
          console.error("[FirestoreCompat] query.onSnapshot error for", basePath, e);
          throw e;
        }
      }
    };
  }

  // The main v8-compatible Firestore wrapper
  const compat = {
    // Store raw modular db for internal use
    _raw: _rawDb,

    // .collection(path) -> returns v8-compatible collection ref
    collection: (collectionPath) => {
      return makeCollectionRef(collectionPath);
    },

    // Expose the modular functions for advanced usage
    _modular: {
      collection,
      doc,
      getDoc,
      getDocs,
      setDoc,
      addDoc,
      updateDoc,
      deleteDoc,
      query,
      where,
      onSnapshot,
      serverTimestamp,
      arrayUnion,
      Timestamp,
      writeBatch
    }
  };

  console.log("[FirestoreCompat] v8-compatible Firestore wrapper created");
  return compat;
}

/**
 * Creates a v8-compatible Auth wrapper for the modular Auth instance.
 * This allows legacy code in app.js that uses `firebaseAuthInstance.signInWithEmailAndPassword()`
 * and similar v8 method-style syntax to work with the modular Firebase v10 SDK.
 *
 * Supported v8 methods mapped to v9+ modular:
 * - .signInWithEmailAndPassword(email, password)
 * - .createUserWithEmailAndPassword(email, password)
 * - .signOut()
 * - .onAuthStateChanged(cb)
 * - .sendPasswordResetEmail(email)
 * - .currentUser (getter)
 * - ._raw (the raw modular Auth instance for modular-SDK consumers)
 */
function createV8CompatibleAuth(modularAuth) {
  const _rawAuth = modularAuth;

  return {
    // Keep the raw modular instance for internal use by modular-SDK consumers
    _raw: _rawAuth,

    // v8 -> modular: sign in with email/password
    signInWithEmailAndPassword: (email, password) =>
      signInWithEmailAndPassword(_rawAuth, email, password),

    // v8 -> modular: create user with email/password
    createUserWithEmailAndPassword: (email, password) =>
      createUserWithEmailAndPassword(_rawAuth, email, password),

    // v8 -> modular: sign out
    signOut: () => signOut(_rawAuth),

    // v8 -> modular: subscribe to auth state changes
    onAuthStateChanged: (cb) => onAuthStateChanged(_rawAuth, cb),

    // v8 -> modular: send password reset email
    sendPasswordResetEmail: (email) => sendPasswordResetEmail(_rawAuth, email),

    // v8 -> modular: expose the current user
    get currentUser() {
      return _rawAuth.currentUser;
    }
  };
}

let _initPromise = null;

function getConfigFromGlobal() {
  if (!globalThis.firebaseConfig) throw new Error("firebaseConfig not found on window/globalThis");
  return globalThis.firebaseConfig;
}

function ensureWindowHealth() {
  if (!globalThis.__firebaseHealth) {
    globalThis.__firebaseHealth = {
      initStartedAt: Date.now(),
      initCompletedAt: null,
      authStateChanges: 0,
      lastAuthUserUid: null,
      lastAuthUserEmail: null,
      lastReadyReason: null
    };
  }
  return globalThis.__firebaseHealth;
}

async function initFirebaseModular() {
  if (_initPromise) return _initPromise;

  _initPromise = (async () => {
    const health = ensureWindowHealth();

    const config = getConfigFromGlobal();

    // Initialize app singleton
    const app =
      getApps().length === 0 ? initializeApp(config) : getApps()[0];

    await initializeAppCheck(app);

    const auth = getAuth(app);
    const isLocalhost = typeof window !== "undefined" && window.location &&
      (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
    let db = null;
    if (isLocalhost) {
      try {
        db = initializeFirestore(app, {
          experimentalAutoDetectLongPolling: true,
          useFetchStreams: false
        });
        console.log("[Firebase Bootstrap] Firestore configured for localhost transport stability (long-polling auto-detect).");
      } catch (firestoreInitError) {
        console.warn("[Firebase Bootstrap] Localhost Firestore transport override skipped:", firestoreInitError && firestoreInitError.message ? firestoreInitError.message : firestoreInitError);
        db = getFirestore(app);
      }
    } else {
      db = getFirestore(app);
    }
    const storage = getStorage(app);

    const authPersistencePreferenceKey = "whatToEatKeepSignedIn";
    globalThis.setFirebaseAuthPersistence = async function (keepSignedIn) {
      await setPersistence(auth, keepSignedIn ? browserLocalPersistence : browserSessionPersistence);
      try {
        globalThis.localStorage.setItem(authPersistencePreferenceKey, keepSignedIn ? "true" : "false");
      } catch (storageError) {
        console.warn("[Firebase Bootstrap] Could not save sign-in persistence preference:", storageError);
      }
    };

    let keepSignedIn = false;
    try {
      keepSignedIn = globalThis.localStorage.getItem(authPersistencePreferenceKey) === "true";
    } catch (storageError) {
      console.warn("[Firebase Bootstrap] Could not read sign-in persistence preference:", storageError);
    }

    try {
      await globalThis.setFirebaseAuthPersistence(keepSignedIn);
    } catch (e) {
      console.warn("[Firebase Bootstrap] setPersistence failed:", e);
    }

    // Google provider
    const googleProvider = new GoogleAuthProvider();
    googleProvider.setCustomParameters({ prompt: "select_account" });

    // Expose compatibility globals
    globalThis.firebaseAppInstance = app;
    // IMPORTANT: Wrap modular auth with v8-compatible methods for legacy app.js code.
    // app.js uses firebaseAuthInstance.signInWithEmailAndPassword() and similar v8 syntax.
    globalThis.firebaseAuthInstance = createV8CompatibleAuth(auth);
    // Also expose the RAW modular Auth instance for firebase-integration.js / firebase-config.js
    // which use modular SDK functions like signInWithEmailAndPassword(firebaseAuth, ...) directly.
    globalThis.firebaseAuthRaw = auth;
    // IMPORTANT: Wrap modular db with v8-compatible .collection() for legacy app.js code.
    // The rest of app.js uses firebaseDbInstance.collection('name').get() and similar v8 syntax.
    globalThis.firebaseDbInstance = createV8CompatibleFirestore(db);
    // Also expose the RAW modular Firestore instance for firebase-integration.js
    // which uses modular SDK functions like collection(firebaseDb, 'name') directly.
    globalThis.firebaseDbRaw = db;
    globalThis.firebaseStorageInstance = storage;
    globalThis.firebaseStorageReady = true;
    globalThis.firebaseGoogleProvider = googleProvider;

    // Expose modular auth functions for legacy scripts (app.js) that expect them on window/globalThis.
    globalThis.signInWithEmailAndPassword = signInWithEmailAndPassword;
    globalThis.createUserWithEmailAndPassword = createUserWithEmailAndPassword;
    globalThis.modularSignOut = signOut;

    globalThis.firebaseReady = true;
    health.initCompletedAt = Date.now();
    health.lastReadyReason = "modular-init-complete";

    // Expose modular Firestore functions for modules that import from window
    // (e.g. js/modules/notifications.js social listeners use window.__firestoreModular).
    globalThis.__firestoreModular = {
      collection, doc, query, where, orderBy, limit,
      onSnapshot, getDoc, getDocs, setDoc, addDoc, updateDoc, deleteDoc,
      serverTimestamp, arrayUnion, Timestamp, writeBatch,
    };

    // Keep a counter for runtime confidence
    onAuthStateChanged(auth, (user) => {
      health.authStateChanges += 1;
      health.lastAuthUserUid = user?.uid ?? null;
      health.lastAuthUserEmail = user?.email ?? null;

      console.log("[Firebase Bootstrap] onAuthStateChanged:", {
        uid: user?.uid ?? null,
        email: user?.email ?? null
      });
    });

    return {
      app,
      auth,
      db,
      storage,
      googleProvider,
      helpers: { serverTimestamp, arrayUnion }
    };
  })();

  return _initPromise;
}

// Expose legacy entrypoints expected by existing code
globalThis.initializeAppCheck = async function initAppCheck(appInstance) {
  const targetApp = appInstance || (globalThis.firebaseAppInstance || getApps()[0] || null);
  return initializeAppCheck(targetApp);
};
globalThis.getFirebaseAppCheckToken = async function getFirebaseAppCheckToken(forceRefresh = false) {
  if (!appCheckInstance || typeof appCheckGetToken !== "function") {
    throw new Error("Firebase App Check is not initialized.");
  }
  return appCheckGetToken(appCheckInstance, forceRefresh);
};
globalThis.initFirebase = async function initFirebase() {
  return initFirebaseModular();
};

globalThis.ensureFirebaseReady = async function ensureFirebaseReady() {
  try {
    await initFirebaseModular();
    return {
      auth: globalThis.firebaseAuthInstance,
      db: globalThis.firebaseDbInstance
    };
  } catch (e) {
    console.error("[Firebase Bootstrap] ensureFirebaseReady failed:", e);
    return null;
  }
};

// Provide getters used by some legacy scripts
globalThis.getFirebaseAuthInstance = () => globalThis.firebaseAuthInstance;
globalThis.getFirebaseDbInstance = () => globalThis.firebaseDbInstance;

// Basic compatibility exports for legacy FieldValue usage (optional)
globalThis.__firebaseServerTimestamp = serverTimestamp;
globalThis.__firebaseArrayUnion = arrayUnion;

// ============ STUB GUARDS: Set placeholder functions BEFORE async init ============
// Prevent "undefined is not a function" errors if app.js calls these before
// the top-level await below completes.
// These will be overwritten with real functions after initFirebaseModular().
if (typeof globalThis.signInWithEmailAndPassword !== 'function') {
  globalThis.signInWithEmailAndPassword = async function stubSignIn() {
    console.warn('[Firebase Bootstrap] signInWithEmailAndPassword called before SDK init — waiting for ready...');
    // Wait for Firebase to be ready, then retry using the real function
    await globalThis.ensureFirebaseReady?.();
    if (typeof globalThis.signInWithEmailAndPassword === 'function' && globalThis.firebaseAuthInstance) {
      return globalThis.signInWithEmailAndPassword(...arguments);
    }
    throw new Error('Firebase SDK not ready after wait');
  };
}

if (typeof globalThis.createUserWithEmailAndPassword !== 'function') {
  globalThis.createUserWithEmailAndPassword = async function stubCreate() {
    console.warn('[Firebase Bootstrap] createUserWithEmailAndPassword called before SDK init — waiting for ready...');
    await globalThis.ensureFirebaseReady?.();
    if (typeof globalThis.createUserWithEmailAndPassword === 'function' && globalThis.firebaseAuthInstance) {
      return globalThis.createUserWithEmailAndPassword(...arguments);
    }
    throw new Error('Firebase SDK not ready after wait');
  };
}

if (typeof globalThis.modularSignOut !== 'function') {
  globalThis.modularSignOut = async function stubSignOut() {
    console.warn('[Firebase Bootstrap] modularSignOut called before SDK init — waiting...');
    await globalThis.ensureFirebaseReady?.();
    if (typeof globalThis.modularSignOut === 'function') {
      return globalThis.modularSignOut();
    }
  };
}

// Also ensure firebaseAuthInstance, firebaseAuthRaw, and firebaseDbInstance are at least defined
if (!globalThis.firebaseAuthInstance) {
  globalThis.firebaseAuthInstance = null;
}
if (!globalThis.firebaseAuthRaw) {
  globalThis.firebaseAuthRaw = null;
}
if (!globalThis.firebaseDbInstance) {
  globalThis.firebaseDbInstance = null;
}
if (!globalThis.firebaseDbRaw) {
  globalThis.firebaseDbRaw = null;
}
globalThis.firebaseReady = false;
// ============ END STUB GUARDS ============

// Auto-init once module loads (config script is loaded before this module)
try {
  await initFirebaseModular();
  console.log("[Firebase Bootstrap] Modular Firebase ready");
} catch (e) {
  console.error("[Firebase Bootstrap] Initialization error:", e);
}
